/** Editorial visit copy, independent of the audio story and its readiness. */
export type AttractionExperience = {
    status: 'draft' | 'published';
    kind: 'standalone' | 'route';
    /** Null keeps the page available but does not feature it on the city home. */
    featuredOrder: number | null;
    title: string;
    summary: string;
    whyGo: string;
    whatToExpect: string;
    whoFor: string;
    tradeoffs: string;
    planning: string;
    checkedAt: string;
    sources: Array<{ title: string; url: string }>;
};

export function parseAttractionExperience(value: unknown): AttractionExperience | null {
    if (value == null) return null;
    const x = value as AttractionExperience;
    if (!['draft', 'published'].includes(x.status) || !['standalone', 'route'].includes(x.kind))
        throw new Error('Invalid experience status or kind');
    if (x.featuredOrder !== null && (!Number.isInteger(x.featuredOrder) || x.featuredOrder < 0))
        throw new Error('featuredOrder must be null or a non-negative integer');
    for (const key of [
        'title',
        'summary',
        'whyGo',
        'whatToExpect',
        'whoFor',
        'tradeoffs',
        'planning',
    ] as const) {
        if (typeof x[key] !== 'string' || !x[key].trim() || x[key].length > 4000)
            throw new Error(`Invalid experience ${key}`);
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(x.checkedAt) || !Number.isFinite(Date.parse(x.checkedAt)))
        throw new Error('Invalid experience checkedAt');
    if (!Array.isArray(x.sources) || !x.sources.length || x.sources.length > 20)
        throw new Error('Experience sources are required');
    for (const source of x.sources) {
        if (
            !source ||
            typeof source.title !== 'string' ||
            !source.title.trim() ||
            typeof source.url !== 'string' ||
            !/^https:\/\//.test(source.url)
        )
            throw new Error('Experience sources must have a title and HTTPS URL');
    }
    return {
        status: x.status,
        kind: x.kind,
        featuredOrder: x.featuredOrder,
        title: x.title.trim(),
        summary: x.summary.trim(),
        whyGo: x.whyGo.trim(),
        whatToExpect: x.whatToExpect.trim(),
        whoFor: x.whoFor.trim(),
        tradeoffs: x.tradeoffs.trim(),
        planning: x.planning.trim(),
        checkedAt: x.checkedAt,
        sources: x.sources.map(({ title, url }) => ({ title: title.trim(), url })),
    };
}
